<form
  [formGroup]="form"
  (ngSubmit)="next()"
  *transloco="let t"
  [scrollOffset]="150"
  [excludedSelectors]="['bgo-static-form']"
  bgoScrollToInvalidOnSubmit
>
  @if (
    {
      priceRangeOffering: offering && staticQuestionForm,
      minimumPriceOffering: offering && offering.type === OfferingType.MinimumPrice && !!offering.fromPrice,
      offeringRequest: offeringQuestionForm || staticQuestionForm,
      otherRequest: !offeringQuestionForm && !staticQuestionForm,
      damageRequest: isDamageRequest,
    };
    as is
  ) {
    <div class="pb-[7rem]">
      @if (!is.priceRangeOffering) {
        <div class="md:block" [class.hidden]="is.minimumPriceOffering">
          <h1>{{ t('createJobRequest.detail.header') }}</h1>
          @if (offering) {
            <h2 class="mt-xs text-gold-500">{{ offering.label }}</h2>
          }
        </div>
      }
      @if (is.offeringRequest) {
        @if (is.priceRangeOffering && staticQuestionForm) {
          <!-- static form (price range offerings), defined in FE -->
          <bgo-static-form
            [offering]="offering!"
            [formGroup]="staticQuestionForm"
            (activated)="activateForm($event)"
            (instantiated)="staticFormInstantiated($event)"
            class="md:pt-3xl lg:pt-4xl"
          ></bgo-static-form>
        } @else if (offeringQuestionForm) {
          <!-- dynamic form (questions come from server) -->
          <bgo-job-request-dynamic-form
            [offeringQuestionForm]="offeringQuestionForm"
            [offering]="offering"
            [isMinimumPriceOffering]="!!is.minimumPriceOffering"
            class="md:mt-3xl"
          ></bgo-job-request-dynamic-form>
        }
      }
      <!-- other job request -->
      @if (is.otherRequest) {
        <div class="grid grid-cols-1 gap-md md:grid-cols-12">
          <div class="md:col-span-7 lg:w-3/4">
            <h3 class="form-section-header">
              {{
                is.damageRequest
                  ? t('createJobRequest.detail.form.damage.header')
                  : t('createJobRequest.detail.form.header')
              }}
            </h3>
            <div class="mt-md font-normal">{{ t('createJobRequest.detail.form.subheader') }}</div>
            <cft-form-field class="mt-lg w-full">
              <label for="title">
                {{
                  is.damageRequest
                    ? t('createJobRequest.detail.form.damage.title')
                    : t('createJobRequest.detail.form.title')
                }}
              </label>
              <input type="text" id="title" formControlName="title" [maxlength]="jobTitleMaxLength" />
            </cft-form-field>
            <div class="h-52 w-full">
              <cft-form-field [autogrow]="true" class="h-full">
                <label for="description">{{
                  is.damageRequest
                    ? t('createJobRequest.detail.form.damage.description')
                    : t('createJobRequest.detail.form.details')
                }}</label>
                <textarea id="description" formControlName="description"></textarea>
                <span class="error">
                  @if (controls.description.errors?.maxlength) {
                    {{
                      t('createJobRequest.detail.form.detailsTooLong', {
                        maxChar: controls.description.errors?.maxlength?.requiredLength,
                      })
                    }}
                  } @else {
                    {{ t('createJobRequest.detail.form.detailsRequired') }}
                  }
                </span>
              </cft-form-field>
            </div>
            <!-- damage questions -->
            @if (is.damageRequest) {
              <div class="mt-xl">
                <label class="heading-md">{{ t('createJobRequest.detail.form.damage.caseNr') }}</label>
                <p class="mt-2xs text-md">{{ t('createJobRequest.detail.form.damage.caseNrHint') }}</p>
                <cft-form-field class="mt-2xs w-full">
                  <label for="caseNumber">{{ t('createJobRequest.detail.form.damage.caseNr') }}</label>
                  <input type="text" id="caseNumber" [formControl]="damageFormControls.caseNumber" [maxlength]="50" />
                </cft-form-field>
              </div>
              <div class="mt-xl">
                <label class="heading-md">{{ t('createJobRequest.detail.form.damage.date') }}</label>
                <p class="mt-2xs text-md">{{ t('createJobRequest.detail.form.damage.dateHint') }}</p>
                <cft-form-field class="mt-2xs w-full">
                  <label for="occurredAt">{{ t('createJobRequest.detail.form.damage.date') }}</label>
                  <input type="date" id="occurredAt" [formControl]="damageFormControls.occurredAt" />
                  <span class="error">{{ t('createJobRequest.detail.form.damage.dateInThePast') }}</span>
                </cft-form-field>
              </div>
              <div class="mt-xl">
                <label class="heading-md">{{ t('createJobRequest.detail.form.damage.directives') }}</label>
                <p class="mt-2xs text-md">{{ t('createJobRequest.detail.form.damage.directivesHint') }}</p>
                <cft-form-field [autogrow]="true" class="mt-2xs h-full w-full">
                  <label for="agentDirectives">{{ t('createJobRequest.detail.form.damage.directives') }}</label>
                  <textarea id="agentDirectives" [formControl]="damageFormControls.agentDirectives" rows="5"></textarea>
                </cft-form-field>
              </div>
            }
          </div>
          <div class="md:col-span-5">
            <div class="sticky top-32">
              <!-- general tips box -->
              @if (!is.damageRequest) {
                <cft-info-box [title]="t('createJobRequest.detail.hint.title')">
                  <span [innerHTML]="t('createJobRequest.detail.hint.content')"></span>
                </cft-info-box>
              }
              <!-- damage contact info -->
              @if (is.damageRequest && agent) {
                <bgo-job-agent-support-box
                  [agent]="agent"
                  [agency]="agent.agency"
                  [logo]="agency?.logo ?? agent.agency?.logo"
                  [damageCaseNumber]="damageFormControls.caseNumber.getRawValue()"
                ></bgo-job-agent-support-box>
              }
            </div>
          </div>
        </div>
      }
      <!-- extra questions (photos, remarks, voucher ...) -->
      <div [class.gradual-reveal-hidden]="staticFormActivated === false">
        <!-- photos -->
        <div class="mt-2xl sm:col-span-8">
          <label class="heading-md" [attr.id]="PhotosAnchorTag">{{ t('createJobRequest.detail.form.photos') }}</label>
          <div class="mt-2xs">
            <div class="text-sm text-primary">
              <span>{{ t('createJobRequest.detail.form.photosTip') }}</span>
            </div>
            <div class="mt-md space-y-sm">
              <div>
                <cft-radio [formControl]="controls.addPhotos" [value]="true">
                  {{ t('yes') }}
                </cft-radio>
                @if (controls.addPhotos.value) {
                  <cft-files
                    class="block py-md"
                    formControlName="files"
                    [accept]="supportedTypes"
                    [generateUploadLink]="generateUploadLink"
                    [deleteFile]="deleteFile"
                    [uploadLabel]="t('createJobRequest.detail.form.photos')"
                  ></cft-files>
                }
              </div>
              <div>
                <cft-radio [formControl]="controls.addPhotos" [value]="false">
                  {{ t('no') }}
                </cft-radio>
              </div>
              @if (controls.addPhotos.invalid && this.controls.addPhotos.touched) {
                <div class="error">
                  {{ t('fieldValidationMessage', {fieldName: '"' + t('createJobRequest.detail.form.photos') + '"'}) }}
                </div>
              }
            </div>
          </div>
        </div>
        <!-- optional info -->
        @if (!is.damageRequest) {
          <div
            class="gradual-reveal absolute right-0 left-0 mt-2xl w-full bg-grey-50 pb-16"
            [class.gradual-reveal-hidden]="staticFormActivated && controls.addPhotos.invalid"
          >
            <div class="h-layout pt-2xl pb-3xl">
              <h3 class="heading-xl">{{ t('createJobRequest.detail.form.optionalInfo') }}</h3>
              <!-- remarks -->
              @if (offeringQuestionForm || staticQuestionForm) {
                <div class="mt-2xl sm:w-3/4">
                  <div class="mt-xs h-32 w-full sm:w-2/3">
                    <cft-form-field [autogrow]="true" class="h-full">
                      <label for="remarks">{{ t('createJobRequest.detail.form.remarksLabel') }}</label>
                      <textarea id="remarks" formControlName="description" class="bg-white!"></textarea>
                      <span class="error">
                        @if (controls.description.errors?.maxlength) {
                          {{
                            t('createJobRequest.detail.form.detailsTooLong', {
                              maxChar: controls.description.errors?.maxlength?.requiredLength,
                            })
                          }}
                        } @else {
                          {{ t('createJobRequest.detail.form.detailsRequired') }}
                        }
                      </span>
                    </cft-form-field>
                  </div>
                </div>
              }
              <!-- voucher -->
              <div class="mt-2xl sm:w-3/4">
                <label [attr.id]="VoucherAnchorTag" class="heading-md">{{
                  t('createJobRequest.detail.form.voucherQuestion')
                }}</label>
                <cft-checkbox class="mt-xs block" [formControl]="controls.hasVoucher">
                  {{ t('createJobRequest.detail.form.voucherCheckbox') }}
                </cft-checkbox>
                @if (controls.hasVoucher.value) {
                  @if ({validation: voucherValidated$ | async}; as ctx) {
                    <div class="mt-xs flex w-full flex-col items-center gap-sm sm:w-2/3 sm:flex-row">
                      <cft-form-field
                        class="w-full"
                        [hasSuccess]="!!ctx.validation?.success"
                        [hasWarning]="!!ctx.validation?.error"
                      >
                        <label for="voucherCode">
                          {{ t('createJobRequest.detail.form.voucherCodeLabel') }}
                        </label>
                        <input id="voucherCode" type="text" [formControl]="controls.voucherCode" class="w-full" />
                        @if (!!ctx.validation?.success) {
                          <p class="success-text">
                            {{ t('createJobRequest.detail.form.voucherValidation.applied') }}
                          </p>
                        }
                        @if (ctx.validation?.error === VoucherValidationError.Expired) {
                          <p class="warn-text">
                            {{ t('createJobRequest.detail.form.voucherValidation.expired') }}
                          </p>
                        }
                        @if (ctx.validation?.error === VoucherValidationError.Invalid) {
                          <p class="warn-text">
                            {{ t('createJobRequest.detail.form.voucherValidation.invalid') }}
                          </p>
                        }
                        @if (ctx.validation?.error === VoucherValidationError.NotFound) {
                          <p class="warn-text">
                            {{ t('createJobRequest.detail.form.voucherValidation.notFound') }}
                          </p>
                        }
                      </cft-form-field>
                      <button
                        type="button"
                        cft-button
                        (click)="validateVoucher(controls.voucherCode.value, {e: $event})"
                        [isLoading$]="voucherValidating$"
                        [disabled]="!controls.voucherCode.value?.length"
                        class="btn-sm btn btn-ghost"
                      >
                        {{ t('createJobRequest.detail.form.voucherApplyButton') }}
                      </button>
                    </div>
                    @if (ctx.validation?.voucher) {
                      <ul class="mt-sm ml-sm">
                        @for (term of ctx.validation?.voucher?.terms ?? []; track term) {
                          <li>
                            <fa-icon [icon]="checkmarkIcon" class="mr-2xs"></fa-icon>
                            @if (term.__typename === 'ServiceFeeVoucherTerm' && !term.clientServiceFee) {
                              {{ t('shared.voucher.terms.serviceFee') }}
                            }
                            @if (term.__typename === 'PriceBasedVoucherTerm' && term.from === 0) {
                              {{
                                t('shared.voucher.terms.priceBased', {
                                  amount: term.discount | bgoRappenToFranc,
                                })
                              }}
                            }
                            @if (term.__typename === 'PriceBasedVoucherTerm' && term.from > 0) {
                              {{
                                t('shared.voucher.terms.priceBasedWithFrom', {
                                  amount: term.discount | bgoRappenToFranc,
                                  from: term.from | bgoRappenToFranc,
                                })
                              }}
                            }
                          </li>
                        }
                      </ul>
                    }
                  }
                }
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }
  <bgo-job-request-footer
    [nextButtonText]="nextButtonText$ | async"
    (cancelButtonClick)="processService.close(t('createJobRequest.close.closeButtonText'))"
    [progressSteps]="progressSteps"
    [showPoweredByBuildigo]="!!agency?.logo"
  ></bgo-job-request-footer>
</form>
